import React, { useRef, useState } from "react";
import BrochurePicker from "../../components/BrochurePicker";
import ReactDOMServer from "react-dom/server";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import StockDocumentPreview from "../../../stock/components/print/StockDocumentPreview";
import { useUploadQuotationDocumentMutation } from "../quotationsApi";

// Generate-and-send flow for an approved Quotation's PDF - reuses the
// SAME letterhead template (StockDocumentPreview.jsx) and html2canvas+
// jsPDF capture recipe already established for LPO printing, per direct
// instruction, rather than a separate one-off Quotation template. docProps
// is built by the parent (QuotationDetail.jsx's buildQuotationPrintProps),
// same "caller builds the props, this just renders them" split as
// StockDocumentPrintModal/LpoDetail.
const QuotationDeliveryModal = ({ show, onClose, docProps, quotation }) => {
  const previewRef = useRef(null);
  const [isSending, setIsSending] = useState(false);
  const [uploadDocument] = useUploadQuotationDocumentMutation();
  // Not stored server-side, so this list exists only for the current send.
  const [brochures, setBrochures] = useState([]);

  if (!quotation || !docProps) return null;

  const handleSend = async () => {
    setIsSending(true);
    try {
      const canvas = await html2canvas(previewRef.current, { scale: 2 });
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;
      pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, imgHeight);
      const pdfBlob = pdf.output("blob");

      await uploadDocument({ recordGuid: quotation.recordGuid, file: pdfBlob, brochures }).unwrap();
      toast.success("Quotation generated and sent");
      onClose();
    } catch (error) {
      toast.error(error?.message || "Could not generate/send the quotation");
    } finally {
      setIsSending(false);
    }
  };

  const handlePrint = () => {
    const win = window.open("", "_blank", "width=900,height=1000");
    if (!win) return;
    win.document.write(
      `<html><head><title>${docProps.title} - ${docProps.docNo}</title>` +
        `<style>@page { size: A4; margin: 10mm; } html,body { margin:0; padding:0; } body { width: 190mm; }</style></head>` +
        `<body onload="window.print();window.close();">${ReactDOMServer.renderToStaticMarkup(<StockDocumentPreview {...docProps} />)}</body></html>`
    );
    win.document.close();
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Quotation Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "#e9ecef" }}>
        <div style={{ border: "1px solid #ccc" }} ref={previewRef}>
          <StockDocumentPreview {...docProps} />
        </div>
      </Modal.Body>
      {/* Outside previewRef so it is never captured into the PDF. */}
      <div className="px-3 pt-2">
        <BrochurePicker files={brochures} onChange={setBrochures} disabled={isSending} />
      </div>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="outline-primary" onClick={handlePrint}>Print</Button>
        <Button variant="primary" onClick={handleSend} disabled={isSending}>
          {isSending ? "Sending..." : "Send Quotation"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QuotationDeliveryModal;
